import type { CSSProperties, ReactNode } from "react";
import { MANUFACTURERS, TIER_COLORS, archetypeById, moduleById, unitById, zombieById, type Manufacturer, type Role, type Chassis } from "@zend/content";
import { pick, t } from "../i18n/index.js";
import type { Tier } from "./types.js";
import { StatIcon } from "../assets/gameIcons.js";

export const tierColor = (tier: Tier): string => TIER_COLORS[tier];
export const mfrColor = (mfr: string): string => MANUFACTURERS[mfr as Manufacturer]?.color ?? "#9aa7b5";
export const mfrName = (mfr: string): string => MANUFACTURERS[mfr as Manufacturer]?.name ?? mfr;

export function unitName(unitId: string): string {
  if (unitId.startsWith("zmb.")) return pick(zombieById(unitId).name);
  if (unitId === "turret") return t("battle.turret");
  return pick(unitById(unitId).name);
}

export function moduleName(moduleId: string): string {
  return pick(moduleById(moduleId).name);
}

export function roleName(role: Role): string {
  return t(`role.${role}`);
}

export function chassisName(chassis: Chassis): string {
  return t(`chassis.${chassis}`);
}

/** Ability description from the archetype template with the unit's parameters filled in. */
export function abilityText(archetype: string, params: Record<string, number>): string {
  const arch = archetypeById(archetype);
  let text = pick(arch.text);
  for (const [k, v] of Object.entries(params)) text = text.replaceAll(`{${k}}`, String(Math.round(v * 100) / 100));
  return text;
}

export function Stars({ stars, size = 12, max = 5 }: { stars: number; size?: number; max?: number }) {
  return (
    <span className="stars" style={{ display: "inline-flex", gap: 1, lineHeight: 1 }} role="img" aria-label={t("units.star.count", { n: stars, max })}>
      {Array.from({ length: max }, (_, index) => index + 1).map((i) => (
        <StatIcon key={i} stat="star" size={Math.max(10, size)} style={{ opacity: i <= stars ? 1 : 0.24, filter: i <= stars ? undefined : "grayscale(1)" }} />
      ))}
    </span>
  );
}

export function TierBadge({ tier, style }: { tier: Tier; style?: CSSProperties }) {
  return (
    <span className="tier-badge" style={{ background: tierColor(tier), color: tier === "S" || tier === "C" || tier === "D" ? "#111" : "#fff", ...style }}>
      {tier}
    </span>
  );
}

export function MfrBadge({ mfr }: { mfr: string }) {
  return (
    <span className="badge" style={{ borderColor: mfrColor(mfr), color: mfrColor(mfr) }} title={mfrName(mfr)}>
      {mfr}
    </span>
  );
}

export function Tabs<T extends string | number>({ tabs, active, onChange, label, testPrefix }: { tabs: T[]; active: T; onChange: (t: T) => void; label: (t: T) => ReactNode; testPrefix: string }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button key={tab} type="button" role="tab" aria-selected={tab === active} tabIndex={tab === active ? 0 : -1} className={`tab ${tab === active ? "active" : ""}`} onClick={() => onChange(tab)}
          onKeyDown={event => { const index = tabs.indexOf(tab); const next = event.key === "ArrowRight" ? (index + 1) % tabs.length : event.key === "ArrowLeft" ? (index - 1 + tabs.length) % tabs.length : event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1 : -1; if (next >= 0) { event.preventDefault(); onChange(tabs[next]!); (event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>("[role=tab]")[next])?.focus(); } }} data-testid={`${testPrefix}-${tab}`}>
          {label(tab)}
        </button>
      ))}
    </div>
  );
}

export function formatPct(v: number): string {
  return `${Math.round(v * 100)}%`;
}
